import React, { useMemo } from "react";

/**
 * ProductSelect — reusable native <select> that groups products by Category
 * using <optgroup> (folder-like grouping). Used across the ERP wherever a
 * product must be picked from Master Data (single source of truth).
 *
 * Props:
 *   products      : array of product dicts (must include id, name, category_id, category_name)
 *   categories    : array of category dicts (must include id, name). Optional — if omitted, groups are derived from products' category_name.
 *   value         : selected product id
 *   onChange      : (productId) => void  OR native event handler
 *   placeholder   : text for the empty first option
 *   testid        : data-testid attribute
 *   className     : css classes
 *   disabled      : boolean
 *   filter        : optional (product) => boolean applied before grouping
 */
export function ProductSelect({
  products = [],
  categories = null,
  value = "",
  onChange,
  placeholder = "Select product...",
  testid,
  className = "cf-input",
  disabled = false,
  filter,
}) {
  const grouped = useMemo(() => {
    const pool = filter ? products.filter(filter) : products;
    // Preserve the incoming category order if provided; otherwise sort A→Z by category name.
    const buckets = new Map();
    for (const p of pool) {
      const key = p.category_id || "__uncategorized__";
      const label = p.category_name || "Uncategorized";
      if (!buckets.has(key)) buckets.set(key, { label, items: [] });
      buckets.get(key).items.push(p);
    }
    const orderedKeys = categories && categories.length
      ? [...categories.map((c) => c.id), "__uncategorized__"]
      : [...buckets.keys()].sort((a, b) => {
          if (a === "__uncategorized__") return 1;
          if (b === "__uncategorized__") return -1;
          return (buckets.get(a).label || "").localeCompare(buckets.get(b).label || "");
        });
    const groups = [];
    for (const key of orderedKeys) {
      const g = buckets.get(key);
      if (!g || g.items.length === 0) continue;
      g.items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      groups.push({ key, ...g });
    }
    // Include any category id present in products but not listed in `categories` (safety net)
    if (categories && categories.length) {
      for (const [key, g] of buckets.entries()) {
        if (orderedKeys.includes(key)) continue;
        g.items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        groups.push({ key, ...g });
      }
    }
    return groups;
  }, [products, categories, filter]);

  const handle = (e) => {
    if (onChange) onChange(e.target.value);
  };

  return (
    <select
      value={value || ""}
      onChange={handle}
      className={className}
      disabled={disabled}
      data-testid={testid}
    >
      <option value="">{placeholder}</option>
      {grouped.map((g) => (
        <optgroup key={g.key} label={g.label}>
          {g.items.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}

export default ProductSelect;
